import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import type { MenuItemProps } from '@mui/material'
import { FormHelperText, ListItemText, ListSubheader, Tooltip } from '@mui/material'
import type { FormControlProps } from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import type { SelectProps } from '@mui/material/Select'
import Select from '@mui/material/Select'
import type { ForwardedRef, ReactNode } from 'react'
import { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomFormControl from '@/components/inputs/CustomFormControl'

export type SelectFieldOption<V> = MenuItemProps & {
  id: string
  value: V
  icon?: ReactNode
  label: string
  description?: string
  tooltip?: string
  disabled?: boolean
}

export type GroupedSelectFieldOptions<V> = {
  groupKey: string
  groupLabel: ReactNode
  options: SelectFieldOption<V>[]
}

export type CustomSelectFieldProps<V> = Omit<SelectProps<V>, 'variant' | 'multiple'> & {
  size?: FormControlProps['size']
  error?: boolean
  helperText?: string
  options: SelectFieldOption<V>[]
  groupedOptions?: GroupedSelectFieldOptions<V>[]
  variant?: 'outlined' | 'outlinedWhite'
  showAllOption?: boolean
}

/**
 * It modifies the MUI SelectField component to add some Sympower customizations:
 *
 * - Adds a new 'outlinedWhite' variant.
 * - Restrict the variant prop to 'outlined' and 'outlinedWhite',
 *
 */
function CustomSelectField<V extends string | ReadonlyArray<string> | number | undefined>(
  {
    fullWidth,
    helperText,
    showAllOption,
    variant = 'outlined',
    disabled,
    color,
    value,
    required,
    options,
    groupedOptions,
    ...props
  }: CustomSelectFieldProps<V>,
  ref: ForwardedRef<HTMLSelectElement>,
) {
  const { error, sx, id, label, size, onOpen, MenuProps, renderValue } = props
  const { t } = useTranslation()

  const labelId = `${id}-label`
  const defaultOptions: SelectFieldOption<V>[] = showAllOption
    ? [{ id: 'all', value: '' as V, label: t('common.all') }]
    : []

  const selectOptions = [...defaultOptions, ...options]

  // Check if any options have descriptions (to conditionally apply styles)
  const hasDescriptions = selectOptions.some((opt) => opt.description)
  const hasIcons = selectOptions.some((opt) => opt.icon)
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined)
  const mergedMenuProps = hasDescriptions
    ? {
        ...MenuProps,
        PaperProps: {
          ...MenuProps?.PaperProps,
          style: {
            ...MenuProps?.PaperProps?.style,
            width: menuWidth,
            maxWidth: menuWidth,
          },
        },
      }
    : MenuProps

  const resolvedRenderValue =
    renderValue ??
    (hasDescriptions
      ? (selected: V) => {
          if (Array.isArray(selected)) {
            return selected
              .map((value) => selectOptions.find((option) => option.value === value)?.label ?? '')
              .filter(Boolean)
              .join(', ')
          }

          const selectedOption = selectOptions.find((option) => option.value === selected)
          if (!selectedOption) {
            return ''
          }

          return selectedOption.icon ? (
            <span style={{ alignItems: 'center', display: 'inline-flex', gap: 8 }}>
              {selectedOption.icon}
              {selectedOption.label}
            </span>
          ) : (
            selectedOption.label
          )
        }
      : undefined)

  const renderMenuItem = ({
    id,
    value,
    label,
    icon,
    description,
    tooltip,
    disabled = false,
    ...itemProps
  }: SelectFieldOption<V>) => (
    <MenuItem
      {...itemProps}
      key={id}
      disabled={disabled}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        ...(hasDescriptions && { py: 1.5 }),
      }}
      value={value}
    >
      {icon}
      {description ? (
        <ListItemText
          primary={label}
          secondary={description}
          secondaryTypographyProps={{
            variant: 'caption',
            color: 'text.secondary',
            sx: { whiteSpace: 'normal' },
          }}
        />
      ) : (
        label
      )}
      {tooltip && (
        <Tooltip title={tooltip}>
          <HelpOutlineIcon fontSize="small" sx={{ color: '#000' }} />
        </Tooltip>
      )}
    </MenuItem>
  )

  const renderGroupedOptions = () =>
    groupedOptions?.flatMap(({ groupKey, groupLabel, options }) => [
      <ListSubheader key={`group-${groupKey}`}>{groupLabel}</ListSubheader>,
      ...options.map(renderMenuItem),
    ])

  return (
    <CustomFormControl
      color={color}
      disabled={disabled}
      error={Boolean(error)}
      fullWidth={fullWidth}
      required={required}
      size={size}
      sx={sx}
      variant={variant}
    >
      <InputLabel id={labelId} shrink={showAllOption}>
        {label}
      </InputLabel>

      <Select
        {...props}
        ref={ref}
        MenuProps={mergedMenuProps}
        color={color}
        disabled={disabled}
        displayEmpty={showAllOption}
        fullWidth={fullWidth}
        labelId={labelId}
        renderValue={resolvedRenderValue}
        required={required}
        sx={{
          ...sx,
          ...(hasIcons && {
            '& .MuiSelect-select': {
              alignItems: 'center',
              display: 'flex',
              gap: 1,
            },
          }),
          // We need to set maxWidth to 100% to remove the label border
          legend: showAllOption ? { maxWidth: '100%' } : '',
        }}
        value={value}
        variant={variant === 'outlinedWhite' ? 'outlined' : variant}
        onOpen={(event) => {
          if (hasDescriptions) {
            const target = event.currentTarget as HTMLElement | null
            if (target) {
              setMenuWidth(target.clientWidth)
            }
          }
          onOpen?.(event)
        }}
      >
        {[...defaultOptions].map(renderMenuItem)}
        {groupedOptions ? renderGroupedOptions() : options.map(renderMenuItem)}
      </Select>
      {error && <FormHelperText>{helperText}</FormHelperText>}
    </CustomFormControl>
  )
}

export default forwardRef(CustomSelectField) as typeof CustomSelectField
